import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import type { CashFlowEntry } from '../types/database';

interface UseCashFlowEntriesOptions {
  companyIds: string | string[] | null;
  userId: string | undefined;
}

/**
 * useCashFlowEntries - Custom hook for managing cash flow entries
 * 
 * Supports multi-company filtering:
 * - Single company: pass single ID
 * - Multiple companies: pass array of IDs
 * - Personal entries: pass null
 * 
 * @param options - Options object or legacy companyIds
 * @param legacyUserId - Legacy userId parameter (deprecated)
 * @returns Entries data, loading state, error state, and CRUD operations
 * 
 * @example
 * ```tsx
 * // New format (preferred)
 * useCashFlowEntries({ companyIds: ['id1', 'id2'], userId: user?.id });
 * 
 * // Legacy format (still supported)
 * useCashFlowEntries('company-id', user?.id);
 * ```
 */
export function useCashFlowEntries(
  options: UseCashFlowEntriesOptions | string | string[] | null,
  legacyUserId?: string
) {
  // Handle both old and new API
  const companyIds = typeof options === 'object' && options !== null && 'companyIds' in options
    ? options.companyIds
    : options;
  const userId = typeof options === 'object' && options !== null && 'userId' in options
    ? options.userId
    : legacyUserId;
  const [entries, setEntries] = useState<CashFlowEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Use ref to track previous companyIds and only refetch if actually changed
  // Initialize with null so the first render will trigger a fetch
  const prevCompanyIdsRef = useRef<string | null>(null);
  const prevUserIdRef = useRef<string | undefined | null>(null);

  const fetchEntries = useCallback(async () => {
    console.log('[useCashFlowEntries] 🔄 Fetching entries:', { 
      companyIds, 
      companyIdsType: typeof companyIds,
      isArray: Array.isArray(companyIds),
      userId 
    });
    
    if (!userId) {
      setEntries([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('cash_flow_entries')
        .select('*')
        .eq('is_simulation', false)
        .order('entry_date', { ascending: false });

      // Handle multi-company filtering
      if (companyIds === null) {
        // Personal entries only (no company)
        console.log('[useCashFlowEntries] 📝 Filtering: Personal entries (no company)');
        query = query.eq('user_id', userId).is('company_id', null);
      } else if (typeof companyIds === 'string') {
        // Single company
        console.log('[useCashFlowEntries] 📝 Filtering: Single company:', companyIds);
        query = query.eq('company_id', companyIds);
      } else if (Array.isArray(companyIds)) {
        // Multiple companies (consolidated view)
        if (companyIds.length === 0) {
          // No companies selected - return empty
          console.log('[useCashFlowEntries] ⚠️ No companies selected, returning empty');
          setEntries([]);
          setLoading(false);
          return;
        } else if (companyIds.length === 1) {
          console.log('[useCashFlowEntries] 📝 Filtering: Single company (from array):', companyIds[0]);
          query = query.eq('company_id', companyIds[0]);
        } else {
          console.log('[useCashFlowEntries] 📝 Filtering: Multiple companies:', companyIds);
          query = query.in('company_id', companyIds);
          // OTIMIZAÇÃO: Limita a 2000 registros mais recentes para múltiplas empresas
          query = query.limit(2000);
        }
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      
      const currencyBreakdown = data?.reduce((acc, e) => {
        const curr = e.currency || 'BRL';
        acc[curr] = (acc[curr] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      console.log('[useCashFlowEntries] ✅ Fetched entries:', { 
        count: data?.length || 0,
        currencyBreakdown,
        currencyBreakdownJSON: JSON.stringify(currencyBreakdown),
        sampleEntries: data?.slice(0, 3).map(e => ({
          id: e.id,
          amount: e.amount,
          currency: e.currency,
          company_id: e.company_id,
          entry_type: e.entry_type,
        }))
      });
      
      setEntries(data || []);
    } catch (err) {
      setError(err as Error);
      console.error('Error fetching cash flow entries:', err);
    } finally {
      setLoading(false);
    }
  }, [companyIds, userId]);

  useEffect(() => {
    // Only fetch if companyIds or userId actually changed (deep comparison)
    const currentCompanyIds = JSON.stringify(companyIds);
    const currentUserId = userId;
    
    if (currentCompanyIds !== prevCompanyIdsRef.current || currentUserId !== prevUserIdRef.current) {
      prevCompanyIdsRef.current = currentCompanyIds;
      prevUserIdRef.current = currentUserId;
      fetchEntries();
    }
  }, [companyIds, userId, fetchEntries]);

  const createEntry = async (
    entry: Omit<CashFlowEntry, 'id' | 'created_at' | 'updated_at'>
  ) => {
    if (!userId) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('cash_flow_entries')
      .insert([{ ...entry, user_id: userId }])
      .select()
      .single();

    if (error) throw error;

    await fetchEntries();
    return data;
  };

  const updateEntry = async (id: string, updates: Partial<CashFlowEntry>) => {
    const { data, error } = await supabase
      .from('cash_flow_entries')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    await fetchEntries();
    return data;
  };

  const deleteEntry = async (id: string) => {
    const { error } = await supabase
      .from('cash_flow_entries')
      .delete()
      .eq('id', id);

    if (error) throw error;

    await fetchEntries();
  };

  return {
    entries,
    loading,
    isLoading: loading, // Alias for consistency
    error,
    createEntry,
    updateEntry,
    deleteEntry,
    refetch: fetchEntries,
  };
}
